// Scherm 12 — het brondetailpaneel.
//
// Vorm uit de dummy (V1 r. 259-365): autoriteitsbalk, samenvatting, link naar
// het origineel, afgeleide trends, afgeleide skills, kernpassages, meer van
// deze uitgever, metadata.
//
// Drie verschillen met de dummy, alle drie uit de veldenplaatsing (scherm 12):
//  · onder het gezagscijfer staan nu de VIER rubriekdimensies die het dragen.
//    Ze stonden bij alle 565 gepubliceerde bronnen gevuld en kwamen nergens in
//    beeld — één cijfer zonder onderbouwing is een oordeel zonder uitleg.
//  · trends en skills zijn klikbaar op id in plaats van een naam-string (D-4).
//  · uit de metadata verdwijnen "Zoekmethode" en "Taal". Taal stond in de dummy
//    als vaste tekst "NL/EN" zónder dat het ooit gemeten werd (0 van de 565);
//    vindmethode leeft alleen in het register op schijf. Wat erbij komt is de
//    HERKOMST van datum en titel — een schatting die zich als meting voordoet
//    is erger dan geen waarde.

import { useEffect, useMemo, useRef, useState } from "react";

import { BetaalmuurPil, WaaropPil, WiePil, heeftBewijsvelden } from "./Bewijs";
import { EmptyReason } from "./Empty";
import { BevindingenPerTrendOud } from "./TrendlaagOud";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";
import { PanelHeader, ReasonBadge, SectionLabel } from "./ui";
import type { ConclusiesResponse, Source } from "../contracts";
import { ONZEKER_LABEL, ONZEKER_UITLEG } from "../lib/schermen";
// Kleuren via de chattokens: in de oude weergave dezelfde waarden, in de
// nieuwe volgen ze licht en donker (testfeedback 03-11, rij 6).
import { color, radius, size, weight } from "./chatTokens";

const DIMENSIONS: { key: keyof NonNullable<Source["scores"]>; label: string; hint: string }[] = [
  {
    key: "credibility",
    label: "Gezag",
    hint: "Hoe zwaar de uitgever en het documenttype wegen (rubriekdimensie source_credibility).",
  },
  {
    key: "sectorRelevance",
    label: "Sectorrelevantie",
    hint: "Hoe direct dit stuk over de sector van de bron gaat.",
  },
  {
    key: "trendSignal",
    label: "Trendsignaal",
    hint: "Hoeveel verandering het beschrijft in plaats van de stand van zaken.",
  },
  {
    key: "vetSignal",
    label: "Mbo-signaal",
    hint: "Hoeveel het zegt over werk op mbo-niveau.",
  },
];

/** Een cijfer met één decimaal en een komma, zoals in het Nederlands. */
const komma = (n: number) => n.toFixed(1).replace(".", ",");

const ROLE_LABEL: Record<string, string> = {
  "trend-primary": "gelezen als trendbron",
  "skills-primary": "gelezen op vaardigheden",
  "skills-supportive": "ondersteunend voor vaardigheden",
  context: "context — wel bewaard, niet uitgelezen op vaardigheden",
  general: "algemeen",
  "trend-only": "alleen gelezen als trendbron",
  arbeidsmarkt: "alleen gelezen op arbeidsmarktcijfers",
};

const ORIENTATION_LABEL: Record<string, string> = {
  forward_looking: "kijkt vooruit",
  current_state: "beschrijft de huidige stand",
  mixed: "beide",
};

const DATE_SOURCE_LABEL: Record<string, string> = {
  document_metadata: "uit het document zelf",
  serp_metadata: "uit de zoekresultaten",
  estimated: "geschat — niet in het document gevonden",
};

const TITLE_SOURCE_LABEL: Record<string, string> = {
  lane: "uit het document zelf",
  page_metadata: "uit de paginagegevens",
  heading: "de eerste kop in het document",
  estimated: "geschat — het document droeg geen titel",
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "datum onbekend";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "datum onbekend";
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
}

const VINDKRACHT_UITLEG: Record<string, string> = {
  sterk: "gevonden onder een kop die letterlijk 'Conclusies' of 'Samenvatting' heet",
  middel: "gevonden onder een kop met de bevindingen of aanbevelingen van de auteur",
  zwak:
    "gevonden onder een kop die de eigen vraag van het document beantwoordt " +
    "(bijvoorbeeld 'Implicaties' of 'Vooruitblik') — geen conclusiehoofdstuk",
};

/** Volgorde waarin de vindkracht weegt bij het afkappen: sterk eerst. */
const VINDKRACHT_ORDE: Record<string, number> = { sterk: 0, middel: 1, zwak: 2 };

/** Hoeveel punten er direct staan voordat "toon alle N" nodig is. */
const PUNTEN_DIRECT = 8;

/**
 * Wat het rapport ZELF concludeert — de genummerde punten, kort en scanbaar.
 *
 * OWNER-FEEDBACK 2026-08-25: dit blok was een muur. Elk punt droeg zijn citaat
 * én zijn sectieherkomst uitgeklapt, en daaronder stond nóg een klapregel met
 * de volledige verbatim sectie. Alles tegelijk op het scherm, en dan leest
 * niemand de lijst.
 *
 * Nu staan alleen de punten er; het BEWIJS zit achter een klik — hetzelfde
 * patroon als de bron-pills in de chat (ChatBonnetje): alle informatie blijft,
 * ze verhuist naar de tweede klik. De verbatim-sectie is VAN HET SCHERM AF maar
 * blijft in de API (`sectieTekst`), want de chat gebruikt hem.
 *
 * Bij meer dan acht punten staan de sterkste acht er. Eén document leverde er
 * dertig (de auteur nummert per ecosysteem opnieuw) en dan is de lijst weer de
 * muur die deze ingreep wegneemt. Het totaal staat er altijd bij, dus je weet
 * wat je niet ziet — en de KEUZE gaat op vindkracht, de VOLGORDE blijft die van
 * het rapport.
 */
function ConclusieBlok({ conclusies }: { conclusies: ConclusiesResponse | null }) {
  const [open, setOpen] = useState<number | null>(null);
  const [alles, setAlles] = useState(false);

  const bevindingen = conclusies?.bevindingen ?? [];
  const zichtbaar = useMemo(() => {
    if (alles || bevindingen.length <= PUNTEN_DIRECT) {
      return bevindingen.map((_, i) => i);
    }
    return bevindingen
      .map((b, i) => ({ i, orde: VINDKRACHT_ORDE[b.vindkracht ?? ""] ?? 1 }))
      // Stabiel: bij gelijke vindkracht wint de volgorde van het rapport.
      .sort((a, b) => a.orde - b.orde || a.i - b.i)
      .slice(0, PUNTEN_DIRECT)
      .map((x) => x.i)
      .sort((a, b) => a - b);
  }, [bevindingen, alles]);

  const kop = <SectionLabel>Wat het rapport zelf concludeert</SectionLabel>;

  if (conclusies === null) {
    return (
      <>
        {kop}
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: size.meta, color: color.faint }}>conclusies laden…</p>
        </div>
      </>
    );
  }

  if (bevindingen.length === 0) {
    return (
      <>
        {kop}
        <div style={{ marginBottom: 20 }}>
          <EmptyReason
            reason={conclusies.reden ?? "conclusies van dit document nog niet opgehaald"}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {kop}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {zichtbaar.map((i) => {
            const b = bevindingen[i]!;
            const isOpen = open === i;
            return (
              <div key={i}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "baseline",
                    width: "100%",
                    textAlign: "left",
                    background: "none",
                    border: "none",
                    borderRadius: radius.quote,
                    padding: "7px 8px 7px 0",
                    font: "inherit",
                    fontSize: size.body,
                    color: color.ink,
                    lineHeight: 1.55,
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = color.surfaceQuote;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "none";
                  }}
                >
                  <span
                    style={{
                      color: color.faint,
                      minWidth: 20,
                      flexShrink: 0,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {b.nummer || `${i + 1}.`}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, color: b.onzeker ? color.soft : undefined }}>
                    {b.bevinding}
                    {/* L2 (bouwrun R1): zonder letterlijk citaat geen stellige weergave. */}
                    {b.onzeker && (
                      <span style={{ marginLeft: 6 }} data-testid="bevinding-onzeker">
                        <ReasonBadge text={ONZEKER_LABEL} tone="amber" title={ONZEKER_UITLEG} />
                      </span>
                    )}
                  </span>
                  <span style={{ color: color.disabled, flexShrink: 0, fontSize: 11 }}>
                    {isOpen ? "▴" : "▾"}
                  </span>
                </button>
                {isOpen && (
                  <div style={{ padding: "0 0 12px 28px" }}>
                    <p
                      style={{
                        fontSize: 12,
                        color: color.muted,
                        lineHeight: 1.65,
                        borderLeft: `2px solid ${color.border}`,
                        paddingLeft: 12,
                        fontStyle: "italic",
                        margin: 0,
                      }}
                    >
                      {b.citaat ? `„${b.citaat}”` : "Geen letterlijk citaat bij deze bevinding."}
                    </p>
                    <p style={{ fontSize: 11, color: color.faint, margin: "5px 0 0" }}>
                      {b.sectie ? `uit: ${b.sectie}` : "sectie niet vastgelegd"}
                      {b.vindkracht && VINDKRACHT_UITLEG[b.vindkracht]
                        ? ` · ${VINDKRACHT_UITLEG[b.vindkracht]}`
                        : ""}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {bevindingen.length > PUNTEN_DIRECT && (
          <button
            type="button"
            onClick={() => setAlles((a) => !a)}
            style={{
              background: "none",
              border: "none",
              padding: "8px 0 0",
              font: "inherit",
              fontSize: size.meta,
              color: color.accentBlue,
              cursor: "pointer",
            }}
          >
            {alles
              ? "toon de sterkste 8"
              : `toon alle ${bevindingen.length} punten`}
          </button>
        )}

        <p style={{ fontSize: 11, color: color.faint, marginTop: 10, lineHeight: 1.5 }}>
          Elk punt is letterlijk uit dit document overgenomen. Klik erop voor het
          citaat en de sectie waar het staat.
          {conclusies.vindkracht && VINDKRACHT_UITLEG[conclusies.vindkracht]
            ? ` De sectie is ${VINDKRACHT_UITLEG[conclusies.vindkracht]}.`
            : ""}
        </p>
      </div>
    </>
  );
}

export function SourceDetail({
  source,
  otherFromPublisher,
  conclusies,
  onClose,
}: {
  source: Source;
  otherFromPublisher: { id: string; title: string; date: string | null }[];
  /** null = nog aan het laden; undefined = deze weergave vraagt ze niet op. */
  conclusies?: ConclusiesResponse | null;
  onClose: () => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;
  }, [source.id]);

  const divider = <div style={{ height: 1, background: color.lineFaint, marginBottom: 20 }} />;
  const zonderTitel = source.title === source.url;
  const toekomst = source.date ? Date.parse(source.date) > Date.now() : false;

  return (
    <div ref={scroller} style={{ height: "100%", overflowY: "auto" }}>
      <PanelHeader onClose={onClose}>
        {source.level && (
          <ReasonBadge
            text={source.level}
            title="Abstractiehoogte: gaat dit stuk over de wereld, over Nederland of over één sector?"
          />
        )}
        <ReasonBadge text={source.publisher} />
        {source.readingRole && (
          <ReasonBadge
            text={ROLE_LABEL[source.readingRole] ?? source.readingRole}
          />
        )}
        {source.fileType && <ReasonBadge text={source.fileType} />}
        {/* Methode v3: wie het zegt en waarop, bij de badges. */}
        <WiePil bron={source} />
        <WaaropPil bron={source} />
        {/* Besluit K2 (reparatie 06-10): de citaten hieronder komen uit dit
            document; het label staat daarom één keer bij de bron. */}
        <BetaalmuurPil bron={source} />
      </PanelHeader>

      <div style={{ padding: 24 }}>
        <h2
          style={{
            fontSize: size.panelTitle,
            fontWeight: weight.bold,
            color: zonderTitel ? color.soft : color.ink,
            lineHeight: 1.3,
            letterSpacing: "-0.02em",
            marginBottom: 4,
            wordBreak: zonderTitel ? "break-all" : "normal",
          }}
        >
          {source.title}
        </h2>
        {zonderTitel ? (
          <p style={{ marginBottom: 14 }}>
            <EmptyReason reason="Deze bron gaf geen titel mee bij het ophalen; hierboven staat de link." />
          </p>
        ) : (
          <p style={{ fontSize: 12, color: color.faint, marginBottom: 14 }}>
            {fmtDate(source.date)}
            {toekomst && (
              <span title="De bron vermeldt zelf deze datum; hij ligt in de toekomst.">
                {" "}
                · datum ligt in de toekomst
              </span>
            )}
            {source.dateSource && ` · ${DATE_SOURCE_LABEL[source.dateSource] ?? source.dateSource}`}
          </p>
        )}

        {/* Autoriteit met de vier cijfers die hem dragen. Methode v3 (wie +
            waarop in de badgerij): het balkje met het gezagscijfer vervalt. */}
        {!heeftBewijsvelden(source) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 12,
          }}
        >
          <span style={{ fontSize: size.meta, color: color.faint, width: 74, flexShrink: 0 }}>
            Autoriteit
          </span>
          <div style={{ flex: 1, height: 6, background: color.lineSoft, borderRadius: 99 }}>
            <div
              style={{
                height: "100%",
                width: `${Math.max(0, Math.min(10, source.authority)) * 10}%`,
                background:
                  source.authority >= 9
                    ? color.ink
                    : source.authority >= 8
                      ? color.body
                      : color.soft,
                borderRadius: 99,
              }}
            />
          </div>
          <span style={{ fontSize: 18, fontWeight: weight.bold, color: color.ink }}>
            {komma(source.authority)}
          </span>
          <span style={{ fontSize: 12, color: color.faint }}>/ 10</span>
        </div>
        )}
        {source.scores ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 6,
              background: color.surfaceQuote,
              borderRadius: radius.block,
              padding: 12,
              marginBottom: 20,
            }}
          >
            {/* Methode v3: de rij "Gezag" vervalt; wie en waarop staan in de
                badgerij. */}
            {DIMENSIONS.filter((d) => !(d.key === "credibility" && heeftBewijsvelden(source))).map((d) => {
              const value = source.scores?.[d.key];
              return (
                <div key={d.key} title={d.hint} style={{ display: "flex", gap: 6 }}>
                  <span style={{ fontSize: 11, color: color.faint, flex: 1 }}>{d.label}</span>
                  <span style={{ fontSize: 11, fontWeight: weight.medium, color: color.body }}>
                    {typeof value === "number" ? komma(value) : "—"}
                  </span>
                </div>
              );
            })}
            {typeof source.scores.composite === "number" && (
              <div
                title="De gewogen totaalscore uit config/source-rubric.yaml."
                style={{
                  display: "flex",
                  gap: 6,
                  gridColumn: "1 / -1",
                  borderTop: `1px solid ${color.border}`,
                  paddingTop: 6,
                  marginTop: 2,
                }}
              >
                <span style={{ fontSize: 11, color: color.faint, flex: 1 }}>Totaalscore</span>
                <span style={{ fontSize: 11, fontWeight: weight.medium, color: color.body }}>
                  {komma(source.scores.composite)}
                </span>
              </div>
            )}
          </div>
        ) : (
          <p style={{ marginBottom: 20 }}>
            <EmptyReason reason="geen rubriekoordeel op dit document gevonden" />
          </p>
        )}

        {divider}

        <SectionLabel>Samenvatting</SectionLabel>
        <div style={{ marginBottom: 20 }}>
          {source.summary ? (
            <p style={{ fontSize: size.body, color: color.muted, lineHeight: 1.7 }}>
              {source.summary}
            </p>
          ) : (
            <EmptyReason reason="Geen leespas op dit document: het is als context bewaard, niet uitgelezen." />
          )}
        </div>

        {/*
          Wat het rapport ZELF concludeert (maandredactie sessie A). Staat
          BEWUST naast "Samenvatting" en niet in plaats daarvan (owner-besluit
          2026-08-25): dat blok is ónze compressie, dit is de stem van de
          auteur. De lezer moet het verschil kunnen zien — dat is de hele reden
          dat de bronclaim-route werkt.
        */}
        {/* undefined = deze weergave vraagt de conclusies niet op (het
            brondetail onder een skill-paneel bijvoorbeeld). Dan hoort er geen
            leeg blok te staan: dat zou een reden tonen die niet waar is. */}
        {conclusies !== undefined && (
          <>
        <ConclusieBlok conclusies={conclusies} />

        {conclusies?.secties.length ? (
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 11, color: color.faint, lineHeight: 1.6 }}>
              Opbouw van het document:{" "}
              {conclusies.secties.map((s) => s.titel || s.labelTekst).join(" · ")}
            </p>
          </div>
        ) : null}
          </>
        )}

        <a
          href={source.url}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-block",
            background: color.ink,
            color: color.surface,
            borderRadius: radius.input,
            padding: "8px 14px",
            fontSize: size.meta,
            textDecoration: "none",
            marginBottom: 20,
          }}
        >
          Bekijk de originele bron ↗
        </a>

        {divider}

        <SectionLabel>{trendlaagAan() ? "Bevindingen" : "Afgeleide trends"} ({source.trends.length})</SectionLabel>
        <div style={{ marginBottom: 20 }}>
          {source.trends.length === 0 ? (
            <EmptyReason reason="Deze bron draagt geen bewijsregel onder een trend; hij leverde vaardigheden op." />
          ) : trendlaagAan() ? (
            <BevindingenPerTrendOud bevindingen={source.trends.map((t) => ({ id: t.id, titel: t.title }))} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {source.trends.map((t) => (
                <div
                  key={t.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: color.surfaceQuote,
                    border: `1px solid ${color.line}`,
                    borderRadius: radius.quote,
                    padding: "8px 10px",
                    fontSize: size.meta,
                    color: color.body,
                  }}
                >
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: weight.bold,
                      color: color.faint,
                      flexShrink: 0,
                    }}
                  >
                    {t.level}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.title}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <SectionLabel>Afgeleide vaardigheden ({source.skills.length})</SectionLabel>
        <div style={{ marginBottom: 20 }}>
          {source.skills.length === 0 ? (
            <EmptyReason reason="Deze bron leverde geen vaardigheden op; hij draagt bewijs onder trends." />
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {source.skills.map((s) => (
                <span
                  key={s.id}
                  title={s.type ?? undefined}
                  style={{
                    fontSize: 11,
                    color: color.body,
                    background: color.surfaceQuote,
                    border: `1px solid ${color.line}`,
                    borderRadius: radius.pill,
                    padding: "3px 9px",
                  }}
                >
                  {s.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {divider}

        <SectionLabel>Relevante passages</SectionLabel>
        <div style={{ marginBottom: 20 }}>
          {source.excerpts?.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {source.excerpts.map((q, i) => (
                <p
                  key={i}
                  style={{
                    fontSize: 12,
                    color: color.muted,
                    lineHeight: 1.65,
                    borderLeft: `2px solid ${color.border}`,
                    paddingLeft: 12,
                    fontStyle: "italic",
                  }}
                >
                  „{q}"
                </p>
              ))}
            </div>
          ) : (
            <EmptyReason reason="geen kernpassages vastgelegd bij deze bron" />
          )}
        </div>

        {otherFromPublisher.length > 0 && (
          <>
            {divider}
            <SectionLabel>
              Meer van {source.publisher} ({otherFromPublisher.length})
            </SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 20 }}>
              {otherFromPublisher.map((o) => (
                <div
                  key={o.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: color.surfaceQuote,
                    borderRadius: radius.quote,
                    padding: "8px 10px",
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 12,
                      color: color.body,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {o.title || "(geen titel)"}
                  </span>
                  <span style={{ fontSize: 11, color: color.faint, flexShrink: 0 }}>
                    {fmtDate(o.date)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {divider}

        <SectionLabel>Herkomst</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {[
            { l: "Uitgever", v: source.publisher },
            { l: "Sectoren", v: source.sectors.join(", ") },
            {
              l: "Gelezen als",
              v: source.readingRole
                ? (ROLE_LABEL[source.readingRole] ?? source.readingRole)
                : null,
            },
            {
              l: "Blikrichting",
              v: source.timeOrientation
                ? (ORIENTATION_LABEL[source.timeOrientation] ?? source.timeOrientation)
                : null,
            },
            { l: "Bestandssoort", v: source.fileType ?? null },
            {
              l: "Datum",
              v: source.date
                ? `${fmtDate(source.date)}${
                    source.dateSource
                      ? ` (${DATE_SOURCE_LABEL[source.dateSource] ?? source.dateSource})`
                      : ""
                  }`
                : null,
            },
            {
              l: "Titel",
              v: source.titleSource
                ? (TITLE_SOURCE_LABEL[source.titleSource] ?? source.titleSource)
                : null,
            },
          ].map((m) => (
            <div key={m.l} style={{ fontSize: 12, color: color.soft }}>
              <span style={{ color: color.faint }}>{m.l}: </span>
              {m.v ?? <EmptyReason reason="niet vastgelegd" />}
            </div>
          ))}
          <div style={{ fontSize: 12, color: color.soft }}>
            <span style={{ color: color.faint }}>Handmatig toegevoegd: </span>
            <EmptyReason reason="niet gemeten — geen veld legt dit vast" />
          </div>
        </div>
      </div>
    </div>
  );
}
